import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  DEFAULT_PORTFOLIO_FILTERS,
  type EsPortfolioFilters,
  type ProcurementTypeFilter,
  type RiskFilter,
  type SingleSourceFilter,
  loadPortfolioFiltersFromStorage,
  normalizePortfolioFilters,
  parsePortfolioFiltersFromSearch,
  portfolioFiltersToSearchParams,
  savePortfolioFiltersToStorage,
} from './esPortfolioFilters'

function mergeFilters(
  base: EsPortfolioFilters,
  patch: Partial<EsPortfolioFilters>
): EsPortfolioFilters {
  return normalizePortfolioFilters({ ...base, ...patch })
}

/**
 * Shared portfolio filter state for Risk Overview and Intelligence View.
 * Hydrates from URL (wins) then sessionStorage; writes both on change.
 */
export function useEsPortfolioFilters() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filtersRef = useRef<EsPortfolioFilters>(DEFAULT_PORTFOLIO_FILTERS)

  const filters = useMemo(() => {
    const fromStorage = loadPortfolioFiltersFromStorage()
    const fromUrl = parsePortfolioFiltersFromSearch(searchParams)
    const merged = mergeFilters(fromStorage, fromUrl)
    filtersRef.current = merged
    return merged
  }, [searchParams])

  const apply = useCallback(
    (patch: Partial<EsPortfolioFilters>, options?: { supplier?: string | null }) => {
      const next = mergeFilters(filtersRef.current, patch)
      filtersRef.current = next
      savePortfolioFiltersToStorage(next)
      const nextParams = portfolioFiltersToSearchParams(next, {
        supplier: options?.supplier === undefined ? searchParams.get('supplier') : options.supplier,
      })
      // Preserve non-portfolio params (none today besides supplier handled above)
      setSearchParams(nextParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Persist whenever URL-driven merge changes (e.g. deep link)
  useEffect(() => {
    savePortfolioFiltersToStorage(filters)
  }, [filters])

  const setRisk = useCallback((risk: RiskFilter) => apply({ risk }), [apply])
  const setCategory = useCallback((category: string) => apply({ category }), [apply])
  const setSubCategory = useCallback((subCategory: string) => apply({ subCategory }), [apply])
  const setBrand = useCallback((brand: string) => apply({ brand }), [apply])
  const setRegion = useCallback((region: string) => apply({ region }), [apply])
  const setLocation = useCallback((location: string) => apply({ location }), [apply])
  const setSingleSource = useCallback(
    (singleSource: SingleSourceFilter) => apply({ singleSource }),
    [apply]
  )
  const setProcurementType = useCallback(
    (procurementType: ProcurementTypeFilter) => apply({ procurementType }),
    [apply]
  )

  const clearAll = useCallback(() => {
    apply({ ...DEFAULT_PORTFOLIO_FILTERS }, { supplier: null })
  }, [apply])

  const setSupplierParam = useCallback(
    (supplierId: string | null) => {
      apply({}, { supplier: supplierId })
    },
    [apply]
  )

  const supplierParam = searchParams.get('supplier') || ''

  return {
    filters,
    apply,
    setRisk,
    setCategory,
    setSubCategory,
    setBrand,
    setRegion,
    setLocation,
    setSingleSource,
    setProcurementType,
    clearAll,
    supplierParam,
    setSupplierParam,
  }
}
